import { JobFileFilterRequest } from './../../modules/reporting/models/job-file-filter-request.model';
import {Injectable} from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import {Observable, of} from 'rxjs';

import {JobFile, JobFilePaginationRequest} from '../models/job-file';
import {API} from '@configs/api.config';
import {ContainerSchedule} from '@shared/models/container-schedule';
import {CloudRunSyncOptimizerToggleService} from '@shared/services/cloud-run-sync-optimizer-toggle.service';
import {PaymentTerm} from '@shared/models/payment-term.model';
import {WorkOrderDefinition} from '@control-tower/models/work-order/work-order-definition.model';
import {getWorkOderDefinitions} from '@control-tower/test-data/jpm.test';
import {map, switchMap, tap} from 'rxjs/operators';
import {CustomerInfo} from '@control-tower/models/customer-info.model';
import {JobSummary} from '../../modules/wizard/models/job-summary.model';
import {EntityMap} from '@shared/models/types';
import {CategorizedLocations} from '@quotation/models/categorized-locations';
import {PreSignedUrl} from "@control-tower/models/jpm/pre-signed-url";
import {RequiredDocumentType} from '../../modules/wizard/models/enums/required-document-type.enum';
import { FileInfo } from '@shared/models/file-info';
import {Pageable} from "@shared/models/pageable.model";
import {User} from "@core/models/user.model";
import {CollaboratorsInvitationModel} from "../../modules/wizard/models/collaborators-invitation.model";
import {Collaborator} from "../../modules/wizard/models/collaborator.model";
import { DistributionWorkflowData } from 'app/modules/wizard/models/distribution-workflow-data.model';
import { JobFileWithLoadAssignmentDetails } from '@core/models/job-file/job-file-with-load-assignment-details.model';
import { ContainerRoute } from '@core/models/job-file/container-route.model';
import { ShipmentOrderSpecFilter } from '../../modules/wizard/store/wizard.actions';
import { PanelSpecsResponse } from '@core/models/job-file/panel-specs-response.model';
import { AssignAllRequest } from '@core/models/job-file/assign-all-request.model';
import { LEG_PATHS_HEADER, LEG_PATHS_IDS_ONLY } from '@core/services/route-polyline.service';
import { OperationSelectionService } from '@core/services/operation-selection.service';

@Injectable({
  providedIn: 'root'
})
export class JobFileService {

  constructor(
    private readonly http: HttpClient,
    private readonly cloudRunSyncToggle: CloudRunSyncOptimizerToggleService,
    private readonly operationSelectionService: OperationSelectionService
  ) {

  }

  getJobFiles(data: JobFilePaginationRequest): Observable<any> {
    return this.http.post(API.jobFile.getJobFiles, data);
  }

  getJobFileSummaries(data: JobFilePaginationRequest): Observable<any> {
    return this.operationSelectionService.getOperationIdsToFilter().pipe(
      switchMap(operationIds => this.http.post(API.jobFile.filterJobFileSummaries, {...data, operationIds}))
    );
  }

  getJobFileByOrgIdAndJobRefId(orgId: string, jobFileId: string): Observable<JobFile> {
    const url = API.jobFile.getJobFileByOrgIdAndJobRefId.format([orgId, jobFileId]);
    return this.http.get<JobFile>(url);
  }

  getJobFileForJobSummaryPage(orgId: string, jobFileId: string): Observable<JobFile> {
    const url = API.jobFile.getJobFileForJobSummaryPage.format([orgId, jobFileId]);
    return this.http.get<JobFile>(url);
  }

  /** Flags the given loads as scheduled, so Control Tower's "Open scheduler" shows them before
   * they have been scheduled. Pass isScheduled false to clear the flag when the load is
   * unassigned. */
  markContainersScheduled(orgId: string, jobRefId: string, containerIds: string[], isScheduled = true): Observable<void> {
    const url = API.jobFile.markContainersScheduled.format([orgId, jobRefId]);
    return this.http.put<void>(url, { containerIds, isScheduled });
  }

  getContainerRoute(orgId: string, jobRefId: string, containerId: string): Observable<ContainerRoute> {
    const url = API.jobFile.getContainerRoute.format([orgId, jobRefId, containerId]);
    return this.http.get<ContainerRoute>(url);
  }

  getShipmentOrderSpecs(orgId: string, jobRefId: string, page = 0, size = 5, filter?: ShipmentOrderSpecFilter): Observable<{content: any[], totalElements: number}> {
    const url = API.jobFile.getShipmentOrderSpecs.format([orgId, jobRefId]);
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    params = this.appendSpecFilterParams(params, filter);
    return this.http.get<{content: any[], totalElements: number}>(url, {params});
  }

  getAllShipmentOrderSpecs(orgId: string, jobRefId: string, filter?: ShipmentOrderSpecFilter): Observable<any[]> {
    const url = API.jobFile.getAllShipmentOrderSpecs.format([orgId, jobRefId]);
    const params = this.appendSpecFilterParams(new HttpParams(), filter);
    return this.http.get<any[]>(url, {params});
  }

  /** Spec ids of all visible orders matching the panel filter (across pages), minus exclusions. */
  getShipmentOrderSpecsPanelIds(orgId: string, jobRefId: string, request: AssignAllRequest): Observable<string[]> {
    const url = API.jobFile.getShipmentOrderSpecsPanelIds.format([orgId, jobRefId]);
    return this.http.post<string[]>(url, request);
  }

  getShipmentOrderSpecsPanel(orgId: string, jobRefId: string, query: {
    page: number; size: number;
    sortBy?: string; sortOrder?: string; groupBy?: string;
    searchBy?: string; searchText?: string;
    deliveryOnOrBefore?: number; deliveryOnOrAfter?: number;
    weightMin?: number; weightMax?: number; cbmMin?: number; cbmMax?: number;
  }): Observable<PanelSpecsResponse> {
    const url = API.jobFile.getShipmentOrderSpecsPanel.format([orgId, jobRefId]);
    let params = new HttpParams()
      .set('page', query.page.toString())
      .set('size', query.size.toString());
    const optional: Array<[string, string | number | undefined]> = [
      ['sortBy', query.sortBy], ['sortOrder', query.sortOrder], ['groupBy', query.groupBy],
      ['searchBy', query.searchBy], ['searchText', query.searchText?.trim()],
      ['deliveryOnOrBefore', query.deliveryOnOrBefore], ['deliveryOnOrAfter', query.deliveryOnOrAfter],
      ['weightMin', query.weightMin], ['weightMax', query.weightMax],
      ['cbmMin', query.cbmMin], ['cbmMax', query.cbmMax]
    ];
    optional.forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        params = params.set(k, v.toString());
      }
    });
    return this.http.get<PanelSpecsResponse>(url, {params});
  }

  getShipmentOrderSpecIds(orgId: string, jobRefId: string, filter?: ShipmentOrderSpecFilter): Observable<string[]> {
    const url = API.jobFile.getShipmentOrderSpecIds.format([orgId, jobRefId]);
    const params = this.appendSpecFilterParams(new HttpParams(), filter);
    return this.http.get<string[]>(url, {params});
  }

  deleteVisibleShipmentOrderSpecsFromJobFile(orgId: string, jobRefId: string, deleteFromOrderManager: boolean = false, filter?: ShipmentOrderSpecFilter) {
    const url = API.jobFile.deleteVisibleShipmentOrderSpecs.format([orgId, jobRefId]);
    // showOnly is implied server-side for delete-visible; only search/date filters are forwarded
    let params = new HttpParams().set('deleteFromOrderManager', deleteFromOrderManager.toString());
    params = this.appendSpecFilterParams(params, filter, false);
    return this.http.post(url, {}, {params});
  }

  private appendSpecFilterParams(params: HttpParams, filter?: ShipmentOrderSpecFilter, includeShowOnly = true): HttpParams {
    if (!filter) {
      return params;
    }
    if (includeShowOnly && filter.showOnly != null) {
      params = params.set('showOnly', filter.showOnly.toString());
    }
    if (filter.searchBy) {
      params = params.set('searchBy', filter.searchBy);
    }
    if (filter.searchText && filter.searchText.trim()) {
      params = params.set('searchText', filter.searchText.trim());
    }
    if (filter.startDate != null && filter.endDate != null) {
      params = params.set('startDate', filter.startDate.toString()).set('endDate', filter.endDate.toString());
    }
    return params;
  }

  getPreviousJobOrg(jobRefId: string): Observable<string> {
    const url = API.jobFile.getPreviousJobOrg.format([jobRefId]);
    return this.http.get(url, { responseType: 'text' });
  }

  getJobFileWithLoadAssignmentDetailsByOrgIdAndJobRefId(orgId: string, jobFileId: string) {
    const url = API.jobFile.getJobFileWithLoadAssignmentDetailsByOrgIdAndJobRefId.format([orgId, jobFileId]);
    return this.http.get<JobFileWithLoadAssignmentDetails>(url);
  }

  getJobFileForLoadAssignmentPage(orgId: string, jobFileId: string) {
    const url = API.jobFile.getJobFileForLoadAssignmentPage.format([orgId, jobFileId]);
    return this.http.get<JobFileWithLoadAssignmentDetails>(url);
  }

  /**
   * @param legPathIdsOnly legs come back with their route id only (no encodedPath) - for screens that don't draw
   *   job file routes; a big job's routes are most of the response. See RoutePolylineService.
   */
  getJobFileByJobRefId(jobFileId: string, legPathIdsOnly = false): Observable<JobFile> {
    const url = API.jobFile.getJobFileByJobRefId.format([jobFileId]);
    return legPathIdsOnly
      ? this.http.get<JobFile>(url, { headers: { [LEG_PATHS_HEADER]: LEG_PATHS_IDS_ONLY } })
      : this.http.get<JobFile>(url);
  }

  getJobFileByOrgIdAndLoadId(orgId: string, loadId: string): Observable<JobFile> {
    const url = API.jobFile.getJobFileByOrgIdAndLoadId.format([orgId, loadId]);
    return this.http.get<JobFile>(url);
  }

  getOrderRefId(workOrderId: string){
    const url = API.jobFile.getOrderRefNumbers.format([workOrderId]);
    return this.http.get(url);
  }

  assignAndSchedule(orgId: string, jobRefId: string, order: string): Observable<ContainerSchedule[]> {
    const url = API.jobFile.assignAndSchedule.format([orgId, jobRefId, order]);
    return this.http.get<ContainerSchedule[]>(url);
  }

  addContractedRateToJobFile(paymentTerm: PaymentTerm, orgId: string, jobRefId: string, rateCardId: string, order: string, generatedBy: string, loadRateCard?: { [key: string]: string; }){
    const contractedRate = {
      rateCardId,
      paymentTerm,
      generatedBy,
      order,
      loadRateCard
    };
    const url = API.jobFile.contractedRate.format([orgId, jobRefId, order]);
   return this.http.post(url, contractedRate);
  }

  getWorkOrderDefinitions(): Observable<WorkOrderDefinition[]> {
    // const url = API.jobFile.getJobFileByOrgIdAndJobRefId.format([orgId, jobFileId]);
    // return this.http.get<JobFile>(url);
    return of(getWorkOderDefinitions(5));
  }

  downloadJobSummary(orgId: string , jobRefId: string, order: string){
    const url = API.jobFile.downloadJobSummary.format([orgId, jobRefId, order]);
    return this.http.get(url, {responseType: 'blob'}).pipe(
      map((res) => new Blob([res], {type: 'application/pdf'}))
    );
  }

  getPDFFileData(url: string) {
    return this.http.get(url, {responseType: 'blob'})
      .pipe(
        map((res) => new Blob([res], {type: 'application/pdf'}))
      );
  }

  getJobSummaryPDFFileInfo(orgId: string , jobRefId: string, order: string): Observable<FileInfo> {
    const url = API.jobFile.downloadJobSummary.format([orgId, jobRefId, order]);
    return this.http.get<FileInfo>(url)
  }

  getPdfPreSIgnedUrl(fileId: string) {
    const url = API.fileHandelar.getPreSignedUrl.format([fileId]);
    return this.http.get<PreSignedUrl>(url);
  }

  generateJobSummaryAsync(orgId: string , jobRefId: string, order: string){
    const url = API.jobFile.generateJobSummaryAsync.format([orgId, jobRefId, order]);
    return this.http.get(url);
  }

  generateCollaboratorsAsync(orgId: string , jobRefId: string, order: string){
    const url = API.jobFile.generateCollaboratorsAsync.format([orgId, jobRefId, order]);
    return this.http.get(url);
  }

  generateJobSummary(orgId: string, jobRefId: string, order: string){
    const url = API.jobFile.downloadJobSummary.format([orgId, jobRefId, order]);
    return this.http.get(url);
  }

  generateQuotationPDF(orgId: string, jobRefId: string, order: string){
    const url = API.quotation.generateSavePDF.format([orgId, jobRefId, order]);
    return this.http.get(url);
  }

  generateContractPDF(orgId: string, jobRefId: string, order: string){
    const url = API.smartContract.generateAndSavePDF.format([orgId, jobRefId, order]);
    return this.http.get(url);
  }

  jobSendForApproval(orgId: string, jobRefId: string, order: string): Observable<boolean> {
    const url = API.jobFile.jobSendForApproval.format([orgId, jobRefId, order]);
    return this.http.post<boolean>(url, null);
  }

  getCustomerInfoByCustomerId(customerId: string): Observable<CustomerInfo> {
    const url = API.organization.getOrganization.format([customerId]);
    return this.http.get<CustomerInfo>(url);
  }

  getJobSummary(orgId: string, jobFileId: string, order: string): Observable<JobSummary> {
    const url = API.jobFile.getJobSummary.format([orgId, jobFileId, order]);
    return this.http.get<JobSummary>(url);
  }

  finalizeJobFile(orgId: string, jobRefId: string, order: string): Observable<JobFile> {
    const url = API.jobFile.finalizeJobFile.format([orgId, jobRefId, order]);
    return this.http.get<JobFile>(url);
  }

  retrieveCategorizedLocationNames(orgId: string, jobFileId: string): Observable<EntityMap<string, CategorizedLocations[]>> {
    const url = API.jobFile.retrieveCategorizedLocationNames.format([orgId, jobFileId]);
    return this.http.get<EntityMap<string, CategorizedLocations[]>>(url);
  }

  saveWizardRowData(orgId: string, jobRefId: string, rowData: EntityMap<string, string>): Observable<JobFile> {
    const url = API.jobFile.saveWizardRowData.format([orgId, jobRefId]);
    return this.http.post<JobFile>(url, rowData);
  }

  saveVehicleCapacitiesInWizard(orgId: string, jobRefId: string, vehicleCapacities: EntityMap<string, {[vehicleType: number]: number}>): Observable<JobFile> {
    const url = API.jobFile.saveWizardVehicleCapacities.format([orgId, jobRefId]);
    return this.http.post<JobFile>(url, vehicleCapacities);
  }

  saveDistributionWorkflowData(orgId: string, jobRefId: string, distributionWorkflowData: DistributionWorkflowData): Observable<JobFile> {
    const url = API.jobFile.saveWizardDistributionWorkflowData.format([orgId, jobRefId]);

    return this.http.post<JobFile>(url, distributionWorkflowData);
  }

  getPackingListExcel(orgId: string, jobRefId: string, containerId: string) {
    const url = API.jobFile.downloadPackingList.format([orgId, jobRefId, containerId]);
    return this.http.get(url, {
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map((response) => {
        const blob = new Blob([response.body], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'packing-list.xlsx';
        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }
        return {
          blob,
          filename,
          headers: response.headers
        };
      })
    );
  }

  getAllPackingListsZip(orgId: string, jobRefId: string) {
    const url = API.jobFile.downloadPackingListZip.format([orgId, jobRefId]);
    return this.http.get(url, {responseType: 'blob'})
      .pipe(
        map((res) => new Blob([res], {type: 'application/zip'}))
      );
  }

  uploadWizardAttachments(files: File[], urlSuffix: string): Observable<PreSignedUrl[]> {
    const data = new FormData();
    for (const file of files) {
      data.append('files', file, file.name);
    }
    const url = `${API.jobFile.uploadWizardAttachments}${urlSuffix}`;
    return this.http.post<PreSignedUrl[]>(url, data);
  }

  removeWizardAttachment(orgId: string, jobRefId: string, attachmentId: string) {
    const url = API.jobFile.removeWizardAttachment.format([orgId, jobRefId, attachmentId]);
    return this.http.delete(url);
  }

  uploadWizardRequiredDocument(file: File, urlSuffix: string): Observable<PreSignedUrl[]> {
    const data = new FormData();
    data.append('file', file, file.name);
    const url = `${API.jobFile.uploadWizardAttachments}${urlSuffix}`;
    return this.http.post<PreSignedUrl[]>(url, data);
  }

  modifyUrlReplacingParameter(url: string, orgId: string, jobRefId: string) {
    const preDefineOrgId = /{orgId}/gi;
    const preDefineJobRefId = /{jobRefId}/gi;
    return url
      .replace(preDefineOrgId, orgId)
      .replace(preDefineJobRefId, jobRefId);
  }

  modifyUploadRequiredDocumentApiUrl(url: string, orgId: string, jobRefId: string, type: RequiredDocumentType) {
    const preDefineOrgId = /{orgId}/gi;
    const preDefineJobRefId = /{jobRefId}/gi;
    const preDefineType = /{type}/gi;
    return url
      .replace(preDefineOrgId, orgId)
      .replace(preDefineJobRefId, jobRefId)
      .replace(preDefineType, type);
  }

  fetchUsersByScopes(scopes: string[], pageNum: number, pageSize: number, searchKey?: string): Observable<Pageable<User>> {
    let url = API.organization.getOrgUserByScopes.format([`${pageNum}`, `${pageSize}`]);

    if (searchKey) {
      url = `${url}&searchKey=${searchKey}`;
    }
    return this.http.post<Pageable<User>>(url, scopes);
  }

  inviteCollaborators(collaboratorsInvitationModel: CollaboratorsInvitationModel): Observable<JobFile> {
    const url = API.jobFile.inviteCollaborators;
    return this.http.post<JobFile>(url, collaboratorsInvitationModel);
  }

  removeCollaborator(orgId: string, jobRefId: string, collaboratorId: string): Observable<JobFile> {
    const url = API.jobFile.removeCollaborator.format([orgId, jobRefId, collaboratorId]);
    return this.http.get<JobFile>(url);
  }

  deleteShipmentOrderSpecFromJobFile(jobRefId: string, shipmentOrderId: string, deleteFromOrderManager: boolean = false) {
    const url = API.jobFile.deleteShipmentOrderFromJobFileWithParam.format([jobRefId, shipmentOrderId]);
    return this.http.post(url, {}, { params: { deleteFromOrderManager: deleteFromOrderManager.toString() } });
  }

  deleteAllShipmentOrderSpecsFromJobFile(jobRefId: string, deleteFromOrderManager: boolean = false) {
    const url = API.jobFile.deleteAllShipmentOrdersFromJobFileWithParam.format([jobRefId]);
    return this.http.post(url, {}, { params: { deleteFromOrderManager: deleteFromOrderManager.toString() } });
  }

  deleteBatchShipmentOrderSpecsFromJobFile(jobRefId: string, shipmentOrderIds: string[], deleteFromOrderManager: boolean = false) {
    const url = API.jobFile.deleteBatchShipmentOrdersFromJobFile.format([jobRefId]);
    return this.http.post(url, shipmentOrderIds, { params: { deleteFromOrderManager: deleteFromOrderManager.toString() } });
  }

  /**
   * Patch ONLY the jobfile's vehicle capacities. Lets the vehicle selector dialogs (wizard-v1 Update /
   * wizard-v2 Add) write the change through to the jobfile immediately, instead of it living in the
   * store until the optimize call.
   */
  saveVehicleCapacities(orgId: string, jobRefId: string, vehicleCapacities: any): Observable<any> {
    const url = API.jobFile.saveVehicleCapacities.format([orgId, jobRefId]);
    return this.http.post<any>(url, vehicleCapacities);
  }

  /**
   * Patch ONLY the standard-vehicle bucket. Internal/external are left as they are, so a screen that
   * holds no digital-twin rows can persist standard vehicles without wiping them.
   */
  saveStandardVehicleCapacities(orgId: string, jobRefId: string, standard: any): Observable<any> {
    const url = API.jobFile.saveStandardVehicleCapacities.format([orgId, jobRefId]);
    return this.http.post<any>(url, standard || {});
  }

  /** Category names + counts for the selector; also seeds the jobfile with the real fleet on first load. */
  initVehicleSelection(orgId: string, jobRefId: string, operationId: string): Observable<any[]> {
    const url = API.jobFile.initVehicleSelection.format([orgId, jobRefId]);
    const params = new HttpParams().set('operationId', operationId || '');
    return this.http.post<any[]>(url, {}, { params });
  }

  /**
   * One page of vehicle rows FROM the jobfile (saved selection + utilization).
   * `ownership` ('INTERNAL' | 'EXTERNAL') pages a single jobfile bucket — needed by screens that list
   * internal and external categories separately (wizard-v1). Omit it for the combined list (wizard-v2).
   */
  getJobFileVehiclePage(
    orgId: string, jobRefId: string, categoryId: string, search: string,
    pageNumber: number = 0, pageSize: number = 20, ownership: string = ''
  ): Observable<any> {
    const url = API.jobFile.getJobFileVehiclePage.format([orgId, jobRefId]);
    const params = new HttpParams()
      .set('categoryId', categoryId || '')
      .set('search', search || '')
      .set('ownership', ownership || '')
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize));
    return this.http.get<any>(url, { params });
  }

  /**
   * `{totalVehicles, modifiedVehicles, globalUtilization}` for the jobfile's stored vehicles. The
   * paginated selector holds no vehicle rows, so this is the only way to know whether changing the
   * global utilization would overwrite individually-edited vehicles.
   */
  getUtilizationImpact(orgId: string, jobRefId: string): Observable<any> {
    const url = API.jobFile.getUtilizationImpact.format([orgId, jobRefId]);
    return this.http.get<any>(url);
  }

  /**
   * Apply the global capacity utilization to the jobfile's stored vehicles without touching the
   * selection. `applyMode` 'ALL' overwrites individually-edited vehicles too; 'NON_MODIFIED' keeps them.
   */
  applyGlobalUtilization(
    orgId: string, jobRefId: string, utilization: number, applyMode: 'ALL' | 'NON_MODIFIED'
  ): Observable<any> {
    const url = API.jobFile.applyGlobalUtilization.format([orgId, jobRefId]);
    const params = new HttpParams()
      .set('utilization', String(Math.max(0, Math.min(100, Math.round(utilization)))))
      .set('applyMode', applyMode);
    return this.http.post<any>(url, {}, { params });
  }

  /**
   * Selector categories split by ownership, read from the jobfile:
   * `{internal|external|standard: {categoryName: {total, selected, categoryId}}}`. Lets a screen render
   * category rows + counts without loading any vehicle data.
   */
  getVehicleSelectionCategories(orgId: string, jobRefId: string): Observable<any> {
    const url = API.jobFile.getVehicleSelectionCategories.format([orgId, jobRefId]);
    return this.http.get<any>(url);
  }

  /**
   * Set how many vehicles of a category are used: the backend selects the first N (by vehicle number)
   * and deselects the rest. Jobfile-backed replacement for slicing an in-memory vehicle array.
   * `ownership` ('INTERNAL' | 'EXTERNAL') scopes it to one bucket so a per-ownership select-all cannot
   * touch the other bucket's vehicles; omit it to cover both.
   */
  setCategorySelectedCount(
    orgId: string, jobRefId: string, categoryId: string, count: number, ownership: string = ''
  ): Observable<any> {
    const url = API.jobFile.setCategorySelectedCount.format([orgId, jobRefId]);
    const params = new HttpParams()
      .set('categoryId', categoryId || '')
      .set('count', String(count))
      .set('ownership', ownership || '');
    return this.http.post<any>(url, {}, { params });
  }

  /** Apply per-vehicle edits (selection / utilization) by id — only these rows change. */
  updateVehicleRows(
    orgId: string, jobRefId: string,
    updates: Array<{ vehicleId: string; isSelected?: boolean; capacityUtilization?: number }>
  ): Observable<any> {
    const url = API.jobFile.updateVehicleRows.format([orgId, jobRefId]);
    return this.http.post<any>(url, updates);
  }

  reArrangeLocationServices(orgId: string, jobRefId: string, containerId: string, reArrangedLocationIds: string[],reArrangedServiceIds: string[], prevIndex: number, currIndex: number) {
    const url = API.jobFile.reArrangeLocationOrder.format([orgId, jobRefId, containerId, prevIndex.toString(), currIndex.toString()]);
    // reArrange optimizes DISTRIBUTION (LF-DI-FTL) jobs server-side (ORToolService.getOrderedRoute).
    // Carry the same per-request sync toggle so those solves go gateway->Cloud Run (true) or GKE (false).
    // Non-DISTRIBUTION jobs just reorder locally and ignore the flag.
    const params = new HttpParams().set('useCloudRunSync', (!!this.cloudRunSyncToggle.useCloudRunSync).toString());
    return this.http.post(url,
      {
        reArrangedLocationIds,
        reArrangedServiceIds
      },
      { params });
  }

  saveOtherReferenceNumber(orgId :string, jobRefId: string, containerId: string, locationId: string, serviceId: string, workOrderId: string, refNumbers: string []) {
    if (workOrderId == null){
      workOrderId = 'wizard';
    }
    const url=API.jobFile.saveOtherReferenceNumbers.format([orgId, jobRefId, containerId, locationId, serviceId, workOrderId]);
    return this.http.post(url, refNumbers);
  }

  deleteOtherReferenceNumber(orgId :string, jobRefId: string, containerId: string, locationId: string, serviceId: string, workOrderId: string, refNumber: string){
    const url = API.jobFile.deleteOtherReferenceNumber.format([orgId, jobRefId, containerId, locationId, serviceId, workOrderId]);
    return this.http.delete(url, { params: { refNumber } });
  }

  saveOrderReferenceNumber(orgId :string, jobRefId: string, containerId: string, locationId: string, serviceId: string, workOrderId: string, refNumbers: string[] ){
    const url=API.jobFile.saveOrderReferenceNumbers.format([orgId, jobRefId, containerId, locationId, serviceId]);
    return this.http.post(url, refNumbers);
  }

  saveRemark(orgId: string, jobRefId: string, containerId: string, remark: string) {
    const url = API.jobFile.saveRemark.format([orgId, jobRefId, containerId]);
    return this.http.post(url, remark);
  }

  saveRemarks(orgId: string, jobRefId: string, containerId: string, remarks: string[]) {
    const url = API.jobFile.saveRemarks.format([orgId, jobRefId, containerId]);
    return this.http.post(url, remarks);
  }

  deleteRemark(orgId: string, jobRefId: string, containerId: string, indexes: number[]) {
    const url = API.jobFile.deleteRemark.format([orgId, jobRefId, containerId]);
    return this.http.delete(url, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: indexes
    } as any);
  }

  getRemarks(orgId: string, jobRefId: string, containerId: string): Observable<string[]> {
    const url = API.jobFile.getRemarks.format([orgId, jobRefId, containerId]);
    return this.http.get<string[]>(url);
  }

  removeOrderFromContainerActivities(orgId: string, jobRefId: string, containerId: string, orderId: string, deleteRelatedActivities?: boolean): Observable<any> {
    let url = API.jobFile.removeOrderFromContainerActivities.format([orgId, jobRefId, containerId, orderId]);
    if (deleteRelatedActivities) {
      url += '?deleteRelatedActivities=true';
    }
    return this.http.delete(url);
  }
}
